<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="custom-modal"
    :style="{ zIndex: zIndex }"
    @click.self="handleOverlayClick"
  >
    <div
      class="modal-content relative overflow-y-auto"
      :class="{
        '!max-w-[300px]': modal.type === 'preload',
        'w-full !max-w-[440px] !p-5': isDialog
      }"
    >
      <div v-if="modal.type === 'custom' && !modal.hideCloseButton" class="absolute top-4 right-4 z-[100]">
        <button
          @click="handleCloseButton"
          class="grid size-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900"
          aria-label="Cerrar"
        >
          <Icon name="mdi:close" />
        </button>
      </div>

      <component
        v-if="modal.type === 'custom' && modal.component"
        :is="modal.component"
        v-bind="modal.props"
        @close="close"
      />

      <template v-else-if="isDialog">
        <h2 class="text-[16px] font-bold leading-snug tracking-tight text-slate-900">{{ modal.title }}</h2>

        <div v-if="modal.message || dialogIcon" class="mt-2.5 flex items-start gap-3">
          <div
            v-if="dialogIcon"
            class="grid size-9 flex-none place-items-center rounded-lg bg-slate-100 text-slate-700"
          >
            <Icon :name="dialogIcon" class="text-xl" />
          </div>
          <div
            v-if="modal.message"
            class="modal-description min-w-0 flex-1 text-[13.5px] leading-relaxed text-slate-600"
            v-html="modal.message"
          />
        </div>

        <div class="mt-5 flex flex-wrap items-center justify-end gap-2">
          <CustomButton
            v-if="modal.type === 'confirm'"
            variant="secondary"
            @click="handleCancel"
          >
            {{ modal.cancelText }}
          </CustomButton>
          <CustomButton @click="handleConfirm">
            {{ modal.type === 'confirm' ? modal.confirmText : modal.buttonText }}
          </CustomButton>
        </div>
      </template>

      <template v-else-if="modal.type === 'preload'">
        <div class="flex flex-col items-center gap-2 py-1 text-center">
          <Icon :name="modal.icon || 'mdi:loading'" class="animate-spin text-2xl text-slate-400" />
          <div v-if="modal.message" class="text-[13px] font-semibold text-slate-600" v-html="modal.message" />
        </div>
      </template>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-v-html -->

<script setup>
import { computed, onMounted, onUnmounted, provide } from 'vue'
import CustomButton from '~/components/UI/CustomButton.vue'
import { modalStack, setModalCallback, useCustomModal } from '~/composables/useCustomModal'

const props = defineProps({
  modal: {
    type: Object,
    required: true
  }
})

const { close: closeModal } = useCustomModal()

const zIndex = computed(() => 1000 + modalStack.indexOf(props.modal) * 10)

// Aviso y confirmación comparten maqueta
const isDialog = computed(() => props.modal.type === 'alert' || props.modal.type === 'confirm')

// `useCustomModal` usa alias ('info' por defecto) que no son nombres de Iconify:
// sin traducirlos el icono sale como hueco vacío.
const ICON_ALIASES = {
  info: 'mdi:information-outline',
  success: 'mdi:check-circle-outline',
  warning: 'mdi:alert-outline',
  error: 'mdi:alert-circle-outline',
  question: 'mdi:help-circle-outline'
}

const dialogIcon = computed(() => {
  const icon = props.modal.icon
  if (!icon) return null
  return icon.includes(':') ? icon : (ICON_ALIASES[icon] ?? null)
})
const isTop = computed(() => modalStack[modalStack.length - 1]?.id === props.modal.id)

provide('modalContext', {
  id: props.modal.id,
  zIndex: zIndex.value,
  close: (result) => closeModal(props.modal.id, result),
  onBeforeClose: (callback) => setModalCallback(props.modal.id, 'onBeforeClose', callback),
  setCloseResult: (result) => setModalCallback(props.modal.id, 'closeResult', result),
  clearOnBeforeClose: () => setModalCallback(props.modal.id, 'onBeforeClose', null)
})

const handleOverlayClick = () => {
  if (props.modal.closeOnOverlay) {
    handleCancel()
  }
}

const handleEscapeKey = (event) => {
  if (event.key === 'Escape' && isTop.value && props.modal.closeOnEscape) {
    handleCancel()
  }
}

const handleConfirm = async () => {
  if (props.modal.onConfirm) props.modal.onConfirm()
  await close()
}

const handleCancel = async () => {
  if (props.modal.onCancel) props.modal.onCancel()
  await close()
}

const close = async (result) => {
  if (props.modal.onBeforeClose) {
    const canClose = await props.modal.onBeforeClose()
    if (canClose === false) return
  }
  closeModal(props.modal.id, result)
}

const handleCloseButton = () => {
  close()
}

onMounted(() => {
  window.addEventListener('keydown', handleEscapeKey)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleEscapeKey)
})
</script>
